import styles from './ArticlesOverviewPanel.module.scss';
import { List, Typography } from 'antd';
import { ArticleLookupState } from '@/redux/articles';
import { NavLink, useNavigate } from 'react-router-dom';
import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatters } from '@/core';

const { Title, Paragraph, Text } = Typography;

interface Props {
  article: ArticleLookupState;
}

function useArticleNavigation(articleId: string) {
  const navigate = useNavigate();

  const navigateToArticle = () => navigate(`/help/${articleId}`);

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;

    if (event.defaultPrevented || target.closest('a, button')) {
      return;
    }

    navigateToArticle();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && event.key === 'Enter') {
      navigateToArticle();
    }
  };

  return { onClick, onKeyDown };
}

export function ArticleOverviewListItem({ article }: Props) {
  const { t } = useTranslation();
  const navigation = useArticleNavigation(article.id);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <List.Item role="link" tabIndex={0} {...navigation}>
      <div className={styles.item}>
        <NavLink to={`/help/${article.id}`}>
          <Title level={4} className={styles.title}>
            {article.title}
          </Title>
        </NavLink>

        {article.shortDescription && (
          <Paragraph
            key={isExpanded ? 'expanded' : 'collapsed'}
            className={styles.description}
            ellipsis={
              isExpanded
                ? false
                : {
                    rows: 1,
                    expandable: true,
                    symbol: t('articles.overview.showMore'),
                    onExpand: (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setIsExpanded(true);
                    },
                  }
            }
          >
            {article.shortDescription}
            {isExpanded && (
              <>
                {' '}
                <Typography.Link
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsExpanded(false);
                  }}
                >
                  {t('articles.overview.showLess')}
                </Typography.Link>
              </>
            )}
          </Paragraph>
        )}

        <Text type="secondary" className={styles.updatedAt}>
          {formatters.dateTime(article.updatedAt)}
        </Text>
      </div>
    </List.Item>
  );
}
